import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { api } from './api';
import { LOCALES, translate } from './i18n/messages';
import { getDeviceId, getDeviceMeta } from './utils/device';
import { useSessionLimitMonitor } from './hooks/useSessionLimitMonitor';
import { applyTheme } from './utils/theme';
import { setActiveCurrency } from './utils/currency';
import {
  TOKEN_KEY,
  LAST_AUTH_AT_KEY,
  clearAuthToken,
  clearAuthSession,
  getStoredToken,
  getStoredUserId,
  notifyAuthChange,
  persistAuthMeta,
  subscribeAuthChanges,
} from './utils/authSync';

const AuthCtx = createContext(null);
const SlipCtx = createContext(null);
const SettingsCtx = createContext(null);
const LocaleCtx = createContext(null);

export const DEFAULT_SETTINGS = {
  oddsFormat: 'european',
  theme: 'dark',
  navigation: 'auto',
  betSlipView: 'auto',
  betSlipLocation: 'bottom',
  showLiveTimer: true,
  showEventTime: true,
  skipClearConfirm: false,
  hideBoostedOdds: false,
  hideBalance: false,
  showUpButton: true,
  casinoProMode: false,
  navInSportSection: true,
  navInCasinoSection: true,
  navInMenu: true,
  eventListView: 'large',
  eventsMinimised: false,
  eventPageView: 'large',
  favouriteSports: [],
};

function normalizeSettings(raw) {
  const merged = { ...DEFAULT_SETTINGS, ...(raw || {}) };
  if (!merged.theme || merged.theme === 'auto') merged.theme = 'dark';
  return merged;
}

function readSettings() {
  try {
    const raw = localStorage.getItem('vb_settings');
    return raw ? normalizeSettings(JSON.parse(raw)) : DEFAULT_SETTINGS;
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(() => {
    const initial = readSettings();
    applyTheme(initial.theme);
    return initial;
  });

  useEffect(() => {
    applyTheme(settings.theme);
  }, [settings.theme]);

  const updateSettings = useCallback((patch) => {
    setSettings((prev) => {
      const next = normalizeSettings({ ...prev, ...patch });
      localStorage.setItem('vb_settings', JSON.stringify(next));
      return next;
    });
  }, []);

  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
    localStorage.setItem('vb_settings', JSON.stringify(DEFAULT_SETTINGS));
    applyTheme(DEFAULT_SETTINGS.theme);
  }, []);

  return (
    <SettingsCtx.Provider value={{ settings, updateSettings, resetSettings }}>
      {children}
    </SettingsCtx.Provider>
  );
}

export const useSettings = () => useContext(SettingsCtx);

const LOCALE_MANUAL_KEY = 'vb_locale_manual';

function readLocale() {
  try {
    const saved = localStorage.getItem('vb_locale');
    return LOCALES.some((l) => l.id === saved) ? saved : 'tr';
  } catch {
    return 'tr';
  }
}

function readLocaleManual() {
  try {
    return localStorage.getItem(LOCALE_MANUAL_KEY) === '1';
  } catch {
    return false;
  }
}

function persistLocale(next, { manual = false } = {}) {
  try {
    localStorage.setItem('vb_locale', next);
    if (manual) localStorage.setItem(LOCALE_MANUAL_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function LocaleProvider({ children }) {
  const [locale, setLocaleState] = useState(readLocale);
  const geoAppliedRef = useRef(false);

  const setLocale = useCallback((next, opts = {}) => {
    if (!LOCALES.some((l) => l.id === next)) return;
    setLocaleState(next);
    persistLocale(next, { manual: opts.manual !== false });
  }, []);

  const translateFn = useCallback((key, params) => translate(locale, key, params), [locale]);

  const currentLocale = useMemo(
    () => LOCALES.find((l) => l.id === locale) || LOCALES[0],
    [locale],
  );

  useEffect(() => {
    const el = document.documentElement;
    el.lang = locale;
    el.setAttribute('translate', 'no');
    el.classList.add('notranslate');
  }, [locale]);

  useEffect(() => {
    if (geoAppliedRef.current) return undefined;
    geoAppliedRef.current = true;
    let cancelled = false;
    const manual = readLocaleManual();
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = window.setTimeout(() => controller?.abort(), 4000);

    (async () => {
      try {
        const res = await fetch('/api/geo', {
          credentials: 'same-origin',
          signal: controller?.signal,
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled || !data) return;
        const suggested = LOCALES.some((l) => l.id === data.suggestedLocale)
          ? data.suggestedLocale
          : 'tr';
        const country = String(data.countryCode || '').toUpperCase();

        // Turkey visitors must see Turkish unless they explicitly picked another language.
        if (country === 'TR' && !manual) {
          const current = readLocale();
          if (current !== 'tr') {
            setLocaleState('tr');
            persistLocale('tr', { manual: false });
          }
          return;
        }

        // First visit (no saved preference): apply geo suggestion.
        const saved = (() => {
          try { return localStorage.getItem('vb_locale'); } catch { return null; }
        })();
        if (!saved && !manual && suggested) {
          setLocaleState(suggested);
          persistLocale(suggested, { manual: false });
        }
      } catch {
        /* ignore geo failures */
      } finally {
        window.clearTimeout(timer);
      }
    })();

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      controller?.abort();
    };
  }, []);

  return (
    <LocaleCtx.Provider value={{ locale, setLocale, locales: LOCALES, currentLocale, t: translateFn }}>
      {children}
    </LocaleCtx.Provider>
  );
}

export const useLocale = () => useContext(LocaleCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => getStoredToken());
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef(token);
  const skipExternalSyncRef = useRef(false);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    let cancelled = false;
    let retryTimer = null;
    const maxTimer = window.setTimeout(() => {
      if (cancelled) return;
      setLoading(false);
    }, 12000);

    const loadMe = async (attempt = 0) => {
      try {
        const u = await api.me(token);
        if (cancelled || !u) return;
        const nextUser = { ...u, balance: Number(u.balance), bonus_balance: Number(u.bonus_balance || 0), wallet_mode: u.wallet_mode === 'bonus' ? 'bonus' : 'cash' };
        setUser(nextUser);
        setActiveCurrency(nextUser.currency);
        persistAuthMeta(nextUser.id);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        const status = Number(err?.status || 0);
        if (status === 401 || status === 403) {
          clearAuthToken();
          setToken(null);
          setUser(null);
          setLoading(false);
          return;
        }
        if (attempt < 2) {
          retryTimer = window.setTimeout(() => loadMe(attempt + 1), Math.min(800 * (2 ** attempt), 4000));
          return;
        }
        setLoading(false);
      }
    };

    loadMe();
    return () => {
      cancelled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
      window.clearTimeout(maxTimer);
    };
  }, [token]);

  const applyExternalAuth = useCallback((nextToken, userHint) => {
    if (nextToken) {
      if (nextToken === tokenRef.current) {
        if (userHint) { setUser(userHint); setActiveCurrency(userHint.currency); }
        return;
      }
      skipExternalSyncRef.current = true;
      setToken(nextToken);
      if (userHint) {
        setUser(userHint);
        setActiveCurrency(userHint.currency);
        setLoading(false);
        persistAuthMeta(userHint.id);
      } else {
        setLoading(true);
      }
    } else if (tokenRef.current) {
      skipExternalSyncRef.current = true;
      clearAuthSession();
      setToken(null);
      setUser(null);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    return subscribeAuthChanges((detail) => {
      if (detail.type === 'logout') {
        applyExternalAuth(null);
        return;
      }
      applyExternalAuth(detail.token, detail.user);
    });
  }, [applyExternalAuth]);

  useEffect(() => {
    const syncFromStorage = () => {
      const stored = getStoredToken();
      if (stored !== tokenRef.current) {
        applyExternalAuth(stored);
      }
    };
    window.addEventListener('focus', syncFromStorage);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') syncFromStorage();
    });
    return () => {
      window.removeEventListener('focus', syncFromStorage);
    };
  }, [applyExternalAuth]);

  const setSession = useCallback((t, u) => {
    localStorage.setItem(TOKEN_KEY, t);
    persistAuthMeta(u?.id);
    setToken(t);
    setUser(u);
    setActiveCurrency(u?.currency);
    setLoading(false);
    if (!skipExternalSyncRef.current) {
      notifyAuthChange({ type: 'login', token: t, user: u });
    }
    skipExternalSyncRef.current = false;
  }, []);

  const login = async (method, loginValue, password) => {
    const res = await api.login({ method, login: loginValue, password, ...getDeviceMeta() });
    if (res.verificationRequired) return res;
    setSession(res.token, res.user);
    return res.user;
  };

  const register = async (payload) => {
    const res = await api.register(payload);
    if (res.verificationRequired) return res;
    setSession(res.token, res.user);
    return res.user;
  };

  const logout = useCallback(() => {
    clearAuthSession();
    setToken(null);
    setUser(null);
    setLoading(false);
    if (!skipExternalSyncRef.current) {
      notifyAuthChange({ type: 'logout' });
    }
    skipExternalSyncRef.current = false;
  }, []);

  useSessionLimitMonitor(token, logout);

  useEffect(() => {
    if (token) return undefined;
    const userId = getStoredUserId();
    if (!userId) return undefined;

    let cancelled = false;
    const since = Number(localStorage.getItem(LAST_AUTH_AT_KEY) || 0);

    const tryResume = async () => {
      if (cancelled || tokenRef.current || document.visibilityState !== 'visible') return;
      try {
        const signal = await api.sessionSignal(userId, since);
        if (!signal?.active) return;
        const resumed = await api.resumeSession({
          userId: Number(userId),
          deviceId: getDeviceId(),
          since,
        });
        if (resumed?.token) {
          setSession(resumed.token, resumed.user);
        }
      } catch {}
    };

    tryResume();
    const intervalId = window.setInterval(tryResume, 10000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') tryResume();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [token, setSession]);

  const refreshUser = useCallback(async (snapshot) => {
    if (!token && !snapshot) return;
    try {
      const u = snapshot && typeof snapshot === 'object' && snapshot.id
        ? snapshot
        : await api.me(token);
      if (!u) return;
      setUser((prev) => {
        const hasMode = Object.prototype.hasOwnProperty.call(u, 'wallet_mode')
          || Object.prototype.hasOwnProperty.call(u, 'walletMode');
        const hasBonus = Object.prototype.hasOwnProperty.call(u, 'bonus_balance')
          || Object.prototype.hasOwnProperty.call(u, 'bonusBalance');
        const nextMode = hasMode
          ? ((u.wallet_mode || u.walletMode) === 'bonus' ? 'bonus' : 'cash')
          : (prev?.wallet_mode === 'bonus' ? 'bonus' : 'cash');
        const nextBonus = hasBonus
          ? Number(u.bonus_balance ?? u.bonusBalance ?? 0)
          : Number(prev?.bonus_balance || 0);
        if (!prev) {
          return {
            ...u,
            balance: Number(u.balance),
            bonus_balance: nextBonus,
            wallet_mode: nextMode,
          };
        }
        const nextDepReq = Number(u.deposit_wager_required ?? prev.deposit_wager_required ?? 0);
        const nextDepProg = Number(u.deposit_wager_progress ?? prev.deposit_wager_progress ?? 0);
        const nextBonReq = Number(u.bonus_wager_required ?? prev.bonus_wager_required ?? 0);
        const nextBonProg = Number(u.bonus_wager_progress ?? prev.bonus_wager_progress ?? 0);
        const prevWagerKey = prev.wager
          ? `${prev.wager.kind}:${prev.wager.required}:${prev.wager.progress}:${prev.wager.complete}`
          : '';
        const nextWager = Object.prototype.hasOwnProperty.call(u, 'wager') ? u.wager : prev.wager;
        const nextWagerKey = nextWager
          ? `${nextWager.kind}:${nextWager.required}:${nextWager.progress}:${nextWager.complete}`
          : '';
        if (
          prev.id === u.id
          && Number(prev.balance) === Number(u.balance)
          && Number(prev.bonus_balance || 0) === nextBonus
          && String(prev.wallet_mode || 'cash') === nextMode
          && prev.email === u.email
          && prev.name === u.name
          && prev.verificationStatus === (u.verificationStatus ?? u.verification_status)
          && prev.accountNumber === (u.accountNumber ?? u.account_number)
          && Number(prev.deposit_wager_required || 0) === nextDepReq
          && Number(prev.deposit_wager_progress || 0) === nextDepProg
          && Number(prev.bonus_wager_required || 0) === nextBonReq
          && Number(prev.bonus_wager_progress || 0) === nextBonProg
          && prevWagerKey === nextWagerKey
        ) {
          return prev;
        }
        return {
          ...prev,
          ...u,
          balance: Number(u.balance),
          bonus_balance: nextBonus,
          wallet_mode: nextMode,
          deposit_wager_required: nextDepReq,
          deposit_wager_progress: nextDepProg,
          bonus_wager_required: nextBonReq,
          bonus_wager_progress: nextBonProg,
          wager: nextWager,
        };
      });
    } catch (err) {
      const status = Number(err?.status || 0);
      if (status === 401 || status === 403) {
        clearAuthToken();
        setToken(null);
        setUser(null);
      }
    }
  }, [token]);

  return <AuthCtx.Provider value={{ user, token, loading, login, setSession, register, logout, refreshUser }}>{children}</AuthCtx.Provider>;
}

export function SlipProvider({ children }) {
  const [items, setItems] = useState([]);
  const [stake, setStake] = useState(10);

  const add = (event, odd) => {
    setItems((prev) => {
      const filtered = prev.filter((i) => i.event_id !== event.id);
      return [...filtered, { event_id: event.id, odd_id: odd.id, label: odd.label, odd_value: odd.value, event }];
    });
  };

  const remove = (eventId) => setItems((prev) => prev.filter((i) => i.event_id !== eventId));
  const clear = () => setItems([]);
  const totalOdds = items.reduce((a, i) => a * i.odd_value, items.length ? 1 : 0);
  const potentialWin = Math.round(stake * totalOdds * 100) / 100;
  const has = (eventId, oddId) => items.some((i) => i.event_id === eventId && i.odd_id === oddId);

  return <SlipCtx.Provider value={{ items, stake, setStake, add, remove, clear, totalOdds, potentialWin, has }}>{children}</SlipCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
export const useSlip = () => useContext(SlipCtx);
